import { Link } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import { VyneSelect } from '@vynedental/design-system'

import VyneTooltip from '../../../components/VyneTooltip/VyneTooltip'
import { PracticeLocationReschedulingConfiguration } from '../../../models/v2/Practice'

import {
    CANCELLATIONS_VALUES,
    coercePriorValueForUnit,
    DAYS_PRIOR_VALUES,
    HOURS_PRIOR_VALUES,
    PRIOR_UNIT_VALUES,
} from './constants'

const moduleName = 'appointment-settings-locations-list'

type Props = {
    configuration: PracticeLocationReschedulingConfiguration
    onUpdate: (configuration: PracticeLocationReschedulingConfiguration) => void
    disabled: boolean
    isOnlineSchedulingWritebackEnabled: boolean
    practiceId: string
    showSidebookingPlaceholder: boolean
}

const CancellationsFields = ({
    configuration,
    onUpdate,
    disabled,
    isOnlineSchedulingWritebackEnabled,
    practiceId,
    showSidebookingPlaceholder,
}: Props) => {
    const showWritebackTooltip = !isOnlineSchedulingWritebackEnabled
    const priorValues = configuration.cancel_prior_unit === 'hours' ? HOURS_PRIOR_VALUES : DAYS_PRIOR_VALUES
    const cancellationPolicyOptions = CANCELLATIONS_VALUES.map(({ value, display }) => ({
        value,
        label: display,
        disabled: value === 'allow' && !isOnlineSchedulingWritebackEnabled,
    }))
    const priorUnitOptions = PRIOR_UNIT_VALUES.map(({ value, display }) => ({ value, label: display }))
    const priorOptions = priorValues.map(({ value, display }) => ({ value, label: display }))

    return (
        <>
            <Grid
                item={true}
                xs={6}
                sm={3}
                md={4}
                lg={2.3}
                xl={2.3}
                className={`${moduleName}__table-item details`}
                style={{ borderBottom: 'none' }}
            >
                <label className={`${moduleName}__input-label`}>
                    Cancellation Policy
                    {showWritebackTooltip && (
                        <span style={{ marginLeft: 4, display: 'inline-flex' }}>
                            <VyneTooltip>
                                <span>
                                    This location cannot accept direct Cancellations or Reschedules because PMS
                                    Writeback is not enabled. To enable Writeback, please visit the{' '}
                                    <Link
                                        style={{ color: '#fff', textDecoration: 'underline' }}
                                        to={`/writeback-settings/${practiceId}`}
                                    >
                                        PMS Writeback Settings
                                    </Link>{' '}
                                    page.
                                </span>
                            </VyneTooltip>
                        </span>
                    )}
                </label>
                <VyneSelect
                    dataTestId="cancel-policy-select"
                    value={configuration.cancel_appointment}
                    onChange={(value: string) => onUpdate({ ...configuration, cancel_appointment: value })}
                    style={{ width: '100%' }}
                    options={cancellationPolicyOptions}
                    disabled={disabled}
                />
            </Grid>
            <Grid
                item={true}
                xs={12}
                sm={6}
                md={3}
                lg={2.3}
                xl={2.3}
                className={`${moduleName}__table-item details`}
                style={{ borderBottom: 'none' }}
            >
                <label className={`${moduleName}__input-label`}>Allow Cancellation Changes Unit</label>
                <VyneSelect
                    dataTestId="cancel-prior-unit-select"
                    value={configuration.cancel_prior_unit}
                    onChange={(value: 'days' | 'hours') =>
                        onUpdate({
                            ...configuration,
                            cancel_prior_unit: value,
                            cancel_prior: coercePriorValueForUnit(configuration.cancel_prior, value),
                        })
                    }
                    style={{ width: '100%' }}
                    options={priorUnitOptions}
                    disabled={disabled}
                />
            </Grid>
            <Grid
                item={true}
                className={`${moduleName}__table-item details`}
                xs={6}
                sm={3}
                md={3}
                lg={2.3}
                xl={2.3}
                style={{ borderBottom: 'none' }}
            >
                <label className={`${moduleName}__input-label`}>Allow Cancellation Changes</label>
                <VyneSelect
                    dataTestId="cancel-prior-select"
                    value={configuration.cancel_prior.toString()}
                    onChange={(value: string) => onUpdate({ ...configuration, cancel_prior: Number(value) })}
                    style={{ width: '100%' }}
                    options={priorOptions}
                    disabled={disabled}
                />
            </Grid>
            {showSidebookingPlaceholder ? (
                <Grid
                    item={true}
                    xs={4}
                    sm={3}
                    md={2}
                    lg={1}
                    className={`${moduleName}__table-item details ${moduleName}__checkbox`}
                    style={{ borderBottom: 'none' }}
                />
            ) : (
                <Grid
                    item={true}
                    className={`${moduleName}__table-item`}
                    sm={3}
                    md={3}
                    lg={1}
                    style={{ borderBottom: 'none' }}
                />
            )}
        </>
    )
}

export default CancellationsFields
